import React, { useState, useEffect, useRef, useMemo } from 'react';

/**
 * CountryPicker — searchable combobox for picking a country.
 *
 * Replaces native <select>'s anemic single-letter typeahead with a
 * real search field: type "germ" or "DE" or "🇩🇪" and the list
 * filters live. Full keyboard nav (Arrow Up/Down, Enter, Escape).
 * Click outside to close. Region groupings preserved when no query.
 *
 * Props:
 *   - value          — current country code (e.g. 'DE')
 *   - onChange       — fn(newCode) when user picks a country
 *   - countries      — { CODE: { name, flag, region, verified, ... } }
 *   - regionOrder    — array of region keys in the order to display
 *   - regionLabels   — { regionKey: 'Display Label' }
 */
export default function CountryPicker({
  value,
  onChange,
  countries,
  regionOrder,
  regionLabels,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlightedCode, setHighlightedCode] = useState(value);

  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const selected = countries[value];

  // Build the displayed item list — either grouped by region (no query)
  // or a flat filtered list (with query). Region headers are non-selectable
  // sentinel objects with no `code`.
  const items = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q) {
      const matches = Object.entries(countries)
        .filter(([code, x]) =>
          code.toLowerCase().includes(q) ||
          x.name.toLowerCase().includes(q)
        )
        // Prefer exact code matches first, then name-prefix matches, then rest
        .sort(([codeA, xA], [codeB, xB]) => {
          const aCodeExact = codeA.toLowerCase() === q ? 0 : 1;
          const bCodeExact = codeB.toLowerCase() === q ? 0 : 1;
          if (aCodeExact !== bCodeExact) return aCodeExact - bCodeExact;
          const aNameStart = xA.name.toLowerCase().startsWith(q) ? 0 : 1;
          const bNameStart = xB.name.toLowerCase().startsWith(q) ? 0 : 1;
          if (aNameStart !== bNameStart) return aNameStart - bNameStart;
          return xA.name.localeCompare(xB.name);
        });
      return matches.map(([code, x]) => ({ code, x }));
    }
    // No query — group by region
    const grouped = [];
    for (const region of regionOrder) {
      const entries = Object.entries(countries).filter(([, x]) => x.region === region);
      if (entries.length === 0) continue;
      grouped.push({ headerRegion: region });
      for (const [code, x] of entries) {
        grouped.push({ code, x });
      }
    }
    return grouped;
  }, [query, countries, regionOrder]);

  // Selectable items only — used for keyboard nav indexing
  const selectable = useMemo(() => items.filter((i) => i.code), [items]);

  // When the filtered list changes, point the highlight at the top
  // selectable item (so Enter immediately picks the most-relevant match)
  useEffect(() => {
    if (selectable.length > 0) {
      setHighlightedCode(selectable[0].code);
    } else {
      setHighlightedCode(null);
    }
  }, [query, selectable.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Click outside closes the dropdown
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Focus the search input when the dropdown opens
  useEffect(() => {
    if (open && inputRef.current) {
      // Defer focus so it survives the same-frame click that opened us
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  // Scroll highlighted item into view as the user navigates
  useEffect(() => {
    if (!open || !highlightedCode || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-code="${highlightedCode}"]`);
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [highlightedCode, open]);

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      setQuery('');
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightedCode) {
        onChange(highlightedCode);
        setOpen(false);
        setQuery('');
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const idx = selectable.findIndex((i) => i.code === highlightedCode);
      const next = selectable[Math.min(idx + 1, selectable.length - 1)];
      if (next) setHighlightedCode(next.code);
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      const idx = selectable.findIndex((i) => i.code === highlightedCode);
      const prev = selectable[Math.max(idx - 1, 0)];
      if (prev) setHighlightedCode(prev.code);
      return;
    }
    if (e.key === 'Home') {
      e.preventDefault();
      if (selectable[0]) setHighlightedCode(selectable[0].code);
      return;
    }
    if (e.key === 'End') {
      e.preventDefault();
      const last = selectable[selectable.length - 1];
      if (last) setHighlightedCode(last.code);
      return;
    }
  };

  return (
    <div
      ref={containerRef}
      style={{ position: 'relative', width: '100%' }}
    >
      {/* Trigger — looks like a styled select */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        style={{
          width: '100%',
          padding: '0.4rem 2rem 0.4rem 0.6rem',
          backgroundColor: '#fefefe',
          border: '1.5px solid #1a1a2e',
          borderRadius: '0.5rem',
          fontFamily: '"Inter", sans-serif',
          fontWeight: 700,
          fontSize: '0.95rem',
          lineHeight: 1.1,
          textAlign: 'left',
          cursor: 'pointer',
          outline: 'none',
          position: 'relative',
          color: '#1a1a2e',
        }}
      >
        <span style={{ marginRight: '0.4rem' }} aria-hidden="true">{selected?.flag}</span>
        {value} · {selected?.name}{selected?.verified ? '' : ' *'}
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            right: '0.6rem',
            top: '50%',
            transform: 'translateY(-50%)',
            fontSize: '0.7rem',
            opacity: 0.6,
            pointerEvents: 'none',
          }}
        >
          ▾
        </span>
      </button>

      {/* Dropdown panel */}
      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            zIndex: 50,
            background: '#fefdfa',
            border: '1.5px solid #1a1a2e',
            borderRadius: '0.55rem',
            boxShadow: '0 4px 6px rgba(26,26,46,0.08), 0 12px 24px rgba(26,26,46,0.12)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            maxHeight: '360px',
          }}
        >
          {/* Search input */}
          <div
            style={{
              padding: '0.5rem 0.5rem 0.4rem',
              borderBottom: '1px solid rgba(26,26,46,0.12)',
              background: '#fefdfa',
            }}
          >
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type a country or code (e.g. DE or Germany)…"
              aria-label="Search countries"
              autoComplete="off"
              spellCheck={false}
              style={{
                width: '100%',
                padding: '0.4rem 0.6rem',
                background: '#ffffff',
                border: '1.5px solid rgba(26,26,46,0.2)',
                borderRadius: '0.4rem',
                fontFamily: '"Inter", sans-serif',
                fontSize: '0.92rem',
                fontWeight: 500,
                color: '#1a1a2e',
                outline: 'none',
              }}
              onFocus={(e) => { e.currentTarget.style.borderColor = '#1e3a8a'; }}
              onBlur={(e)  => { e.currentTarget.style.borderColor = 'rgba(26,26,46,0.2)'; }}
            />
          </div>

          {/* List */}
          <div
            ref={listRef}
            role="listbox"
            tabIndex={-1}
            aria-activedescendant={highlightedCode ? `country-opt-${highlightedCode}` : undefined}
            style={{
              overflowY: 'auto',
              flex: 1,
              outline: 'none',
            }}
          >
            {items.length === 0 ? (
              <div
                style={{
                  padding: '1rem',
                  textAlign: 'center',
                  color: 'rgba(26,26,46,0.6)',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '0.88rem',
                }}
              >
                No countries match "{query}"
              </div>
            ) : (
              items.map((item, idx) => {
                if (item.headerRegion) {
                  return (
                    <div
                      key={`hdr-${item.headerRegion}`}
                      style={{
                        padding: '0.4rem 0.7rem 0.2rem',
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        color: 'rgba(26,26,46,0.55)',
                        background: 'rgba(26,26,46,0.025)',
                      }}
                    >
                      {regionLabels[item.headerRegion]}
                    </div>
                  );
                }
                const { code, x } = item;
                const isHighlighted = code === highlightedCode;
                const isSelected = code === value;
                return (
                  <button
                    key={code}
                    id={`country-opt-${code}`}
                    type="button"
                    role="option"
                    data-code={code}
                    aria-selected={isSelected}
                    onMouseEnter={() => setHighlightedCode(code)}
                    onClick={() => {
                      onChange(code);
                      setOpen(false);
                      setQuery('');
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      width: '100%',
                      padding: '0.45rem 0.7rem',
                      background: isHighlighted ? 'rgba(30, 58, 138, 0.1)' : 'transparent',
                      border: 'none',
                      borderLeft: isSelected ? '3px solid #1e3a8a' : '3px solid transparent',
                      cursor: 'pointer',
                      textAlign: 'left',
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '0.9rem',
                      color: '#1a1a2e',
                      transition: 'background 0.08s',
                    }}
                  >
                    <span aria-hidden="true" style={{ fontSize: '1rem', flexShrink: 0 }}>{x.flag}</span>
                    <span style={{ fontWeight: 700, minWidth: '2.2rem', color: 'rgba(26,26,46,0.7)' }}>
                      {code}
                    </span>
                    <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {x.name}
                    </span>
                    {!x.verified && (
                      <span
                        title="Estimated rate"
                        style={{
                          fontSize: '0.7rem',
                          color: '#b45309',
                          fontWeight: 700,
                          flexShrink: 0,
                        }}
                      >
                        *
                      </span>
                    )}
                  </button>
                );
              })
            )}
          </div>

          {/* Footer hint */}
          <div
            style={{
              padding: '0.35rem 0.7rem',
              borderTop: '1px solid rgba(26,26,46,0.12)',
              background: 'rgba(26,26,46,0.03)',
              fontFamily: 'Inter, sans-serif',
              fontSize: '0.7rem',
              color: 'rgba(26,26,46,0.6)',
              display: 'flex',
              gap: '0.7rem',
              justifyContent: 'center',
              flexWrap: 'wrap',
            }}
          >
            <span><kbd style={kbdStyle}>↑↓</kbd> navigate</span>
            <span><kbd style={kbdStyle}>↵</kbd> select</span>
            <span><kbd style={kbdStyle}>esc</kbd> close</span>
          </div>
        </div>
      )}
    </div>
  );
}

const kbdStyle = {
  display: 'inline-block',
  padding: '0.05rem 0.32rem',
  background: '#fefefe',
  border: '1px solid rgba(26,26,46,0.2)',
  borderRadius: '0.25rem',
  fontFamily: 'ui-monospace, monospace',
  fontSize: '0.68rem',
  fontWeight: 700,
  color: 'rgba(26,26,46,0.75)',
  marginRight: '0.2rem',
};
